import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { ChevronLeft, ChevronRight, Camera, Film, LayoutGrid, Play } from 'lucide-react'
import { useGallery, useVideos } from '@/lib/hooks'
import { VideoReel } from '@/components/sections/VideoReel'
import { PageHero } from '@/components/layout/PageHero'
import { cn } from '@/lib/utils'
import type { GalleryItem, VideoClip } from '@/types/api'

const PAGE_SIZE = 15

type Tab = 'all' | 'photos' | 'videos' | string

export function GalleryPage() {
  const { data: gallery = [] } = useGallery()
  const { data: videos = [] } = useVideos()
  const [tab, setTab] = useState<Tab>('all')
  const [page, setPage] = useState(1)
  const [lightboxIndex, setLightboxIndex] = useState(-1)

  // Categories derived from data
  const categories = useMemo(() => {
    const set = new Set<string>()
    gallery.forEach((g) => g.category && set.add(g.category))
    return Array.from(set).sort()
  }, [gallery])

  // Combine items into a unified mixed feed for the "All" tab
  const items = useMemo(() => {
    if (tab === 'videos') {
      return videos.map((v): FeedItem => ({ kind: 'video', video: v }))
    }
    if (tab === 'photos') {
      return gallery.map((g): FeedItem => ({ kind: 'photo', photo: g }))
    }
    if (tab === 'all') {
      // interleave: 3 photos, 1 video, 3 photos, 1 video ...
      const feed: FeedItem[] = []
      const photos = [...gallery]
      const vids = [...videos]
      while (photos.length || vids.length) {
        for (let i = 0; i < 3 && photos.length; i++) feed.push({ kind: 'photo', photo: photos.shift()! })
        if (vids.length) feed.push({ kind: 'video', video: vids.shift()! })
      }
      return feed
    }
    // filter by category
    return gallery
      .filter((g) => g.category === tab)
      .map((g): FeedItem => ({ kind: 'photo', photo: g }))
  }, [tab, gallery, videos])

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  // Build a flat photo array (for lightbox) covering ALL photos in current filter
  const lightboxPhotos = useMemo(
    () => items.filter((i): i is Extract<FeedItem, { kind: 'photo' }> => i.kind === 'photo').map((i) => i.photo),
    [items]
  )

  function setTabAndReset(next: Tab) {
    setTab(next)
    setPage(1)
  }

  return (
    <>
      <PageHero
        eyebrow="Moments From The Pitch"
        title="Gallery &amp; Videos."
        intro="Matches, tournaments, tours, training sessions, and community events — the growing archive of the Cosmos family."
        image="https://cosmosfc.org/wp-content/uploads/2022/03/jj01-1024x352.jpg"
        crumbs={[{ label: 'Gallery' }]}
      />

      <section className="py-14">
        <div className="container-narrow">
          {/* Filter tabs */}
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <TabButton active={tab === 'all'} onClick={() => setTabAndReset('all')} icon={<LayoutGrid className="h-3.5 w-3.5" />}>
              All ({gallery.length + videos.length})
            </TabButton>
            <TabButton active={tab === 'photos'} onClick={() => setTabAndReset('photos')} icon={<Camera className="h-3.5 w-3.5" />}>
              Photos ({gallery.length})
            </TabButton>
            <TabButton active={tab === 'videos'} onClick={() => setTabAndReset('videos')} icon={<Film className="h-3.5 w-3.5" />}>
              Videos ({videos.length})
            </TabButton>
            <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
            {categories.map((c) => {
              const count = gallery.filter((g) => g.category === c).length
              return (
                <TabButton key={c} active={tab === c} onClick={() => setTabAndReset(c)}>
                  {c} ({count})
                </TabButton>
              )
            })}
          </div>

          {/* Meta bar */}
          <div className="flex items-center justify-between mb-6 text-xs text-muted-foreground uppercase tracking-widest">
            <span>
              Showing {(currentPage - 1) * PAGE_SIZE + 1}
              –{Math.min(currentPage * PAGE_SIZE, items.length)} of {items.length}
            </span>
            <span>
              Page {currentPage} of {totalPages}
            </span>
          </div>

          {/* Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${tab}-${currentPage}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] gap-3"
            >
              {pageItems.map((item, i) => (
                <FeedTile
                  key={item.kind === 'photo' ? item.photo.src : item.video.id}
                  item={item}
                  onPhotoClick={() => {
                    const idx = lightboxPhotos.findIndex((p) => item.kind === 'photo' && p.src === item.photo.src)
                    setLightboxIndex(Math.max(0, idx))
                  }}
                  delay={(i % 8) * 0.03}
                />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
              <PageBtn onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={cn(
                    'h-9 min-w-[36px] px-3 rounded-md text-sm font-display border transition-colors',
                    p === currentPage
                      ? 'bg-cosmos-green text-white border-cosmos-green'
                      : 'bg-card text-foreground border-border hover:border-cosmos-green hover:text-cosmos-green'
                  )}
                >
                  {p}
                </button>
              ))}
              <PageBtn onClick={() => setPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                Next <ChevronRight className="h-4 w-4" />
              </PageBtn>
            </div>
          )}
        </div>
      </section>

      {/* Featured videos strip when on video tab */}
      {tab === 'videos' && videos.some((v) => v.featured) && (
        <section className="py-16 bg-pitch-950 text-white">
          <div className="container-narrow">
            <div className="mb-8">
              <span className="eyebrow eyebrow-gold">Featured Reels</span>
              <h2 className="mt-2 font-display text-3xl uppercase">Watch on Repeat.</h2>
            </div>
            <VideoReel videos={videos.filter((v) => v.featured)} />
          </div>
        </section>
      )}

      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={lightboxPhotos.map((g) => ({ src: g.src, alt: g.caption, title: g.caption }))}
      />
    </>
  )
}

/* -------------- helpers -------------- */

type FeedItem = { kind: 'photo'; photo: GalleryItem } | { kind: 'video'; video: VideoClip }

function TabButton({
  children,
  active,
  onClick,
  icon,
}: {
  children: React.ReactNode
  active: boolean
  onClick: () => void
  icon?: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all',
        active
          ? 'bg-cosmos-green text-white shadow-md scale-[1.02]'
          : 'bg-card text-foreground border border-border hover:border-cosmos-green hover:text-cosmos-green'
      )}
    >
      {icon}
      {children}
    </button>
  )
}

function PageBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 h-9 px-3 rounded-md text-xs font-bold uppercase tracking-widest border border-border bg-card hover:border-cosmos-green hover:text-cosmos-green disabled:opacity-40 disabled:pointer-events-none transition-colors"
    >
      {children}
    </button>
  )
}

function FeedTile({
  item,
  onPhotoClick,
  delay,
}: {
  item: FeedItem
  onPhotoClick: () => void
  delay: number
}) {
  if (item.kind === 'photo') {
    const g = item.photo
    return (
      <motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.35 }}
        onClick={onPhotoClick}
        className={cn(
          'relative rounded-lg overflow-hidden group bg-pitch-800',
          g.size === 'wide' && 'md:col-span-2',
          g.size === 'tall' && 'row-span-2'
        )}
      >
        <img
          src={g.src}
          alt={g.caption}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute bottom-3 left-3 right-3 text-white font-display text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
          {g.caption}
          {g.category && (
            <span className="block text-[10px] text-cosmos-gold mt-1">{g.category}</span>
          )}
        </div>
      </motion.button>
    )
  }

  // Video tile
  const v = item.video
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="relative rounded-lg overflow-hidden bg-black md:col-span-2"
    >
      <video
        src={v.src}
        poster={v.poster}
        muted={v.muted}
        loop={v.loop}
        autoPlay={v.autoplay}
        playsInline
        controls
        preload="metadata"
        controlsList="nodownload"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute top-2 left-2 z-10 pointer-events-none">
        <span className="inline-flex items-center gap-1 rounded bg-cosmos-gold text-pitch-950 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
          <Play className="h-3 w-3" /> Video
        </span>
      </div>
    </motion.div>
  )
}
